/**
 * Auth API Custom Hook
 * 
 * @description
 * Auth 및 Terms 관련 API를 호출하는 Custom Hook
 * 
 * @phase Phase 8 (API 연동)
 * 
 * @features
 * - 토큰 재발급 (POST /auth/access-token)
 * - 이메일 인증 코드 발송 (POST /auth/send-verification-email)
 * - 이메일 인증 코드 검증 (POST /auth/verify-verification-code)
 * - 비밀번호 재설정 요청 (POST /auth/password-reset)
 * - 비밀번호 변경 (POST /user/change-password)
 * - 계정 삭제 인증 코드 발송 (POST /auth/send-deletion-code)
 * - 계정 삭제 인증 코드 검증 (POST /auth/verify-deletion-code)
 * - 이용약관 리스트 조회 (GET /terms/list)
 * 
 * @usage
 * const { refreshAccessToken, sendVerificationEmail, verifyVerificationCode, ... } = useAuth();
 * 
 * @api
 * Base URL: process.env.REACT_APP_API_BASE_URL
 * 인증 필요: sendVerificationEmail, verifyVerificationCode, sendDeletionCode, verifyDeletionCode
 */

import {
	CreateAccessTokenByRefreshTokenRequestDto,
	CreateAccessTokenByRefreshTokenResponseDto,
	SendVerificationEmailRequestDto,
	SendVerificationEmailResponseDto,
	VerifyVerificationCodeRequestDto,
	VerifyVerificationCodeResponseDto,
	PasswordResetRequestDto,
	PasswordResetRequestResponseDto,
	SendDeletionCodeResponseDto,
	VerifyDeletionCodeRequestDto,
	VerifyDeletionCodeResponseDto,
} from '../../types/auth';

import { ChangePasswordRequestDto, ChangePasswordResponseDto } from '../../types/user';
import { GetTermsListResponseDto } from '../../types/terms';

import apiClient, { ApiError } from '../../services/apiClient';
import { useTranslation } from 'react-i18next';

interface CheckEmailResponseDto {
	isAvailable: boolean;
	message?: string;
}

function extractApiErrorMessage(error: unknown): string {
	if (error instanceof ApiError) {
		const body = error.data as Record<string, unknown> | null | undefined;
		if (body && typeof body.message === 'string' && body.message.trim()) {
			return body.message.trim();
		}
		return error.message;
	}
	if (error instanceof Error) return error.message;
	return '';
}

/**
 * Auth API Custom Hook
 * 
 * @returns Auth API 함수들
 */
export const useAuth = () => {
	const { i18n } = useTranslation();
	const currentLanguage = (i18n.language || 'en').toLowerCase().startsWith('ko') ? 'ko' : 
	                       (i18n.language || 'en').toLowerCase().startsWith('en') ? 'en' :
	                       (i18n.language || '').toLowerCase().startsWith('ar') ? 'ar' :
	                       (i18n.language || '').toLowerCase().startsWith('id') ? 'id' :
	                       (i18n.language || '').toLowerCase().startsWith('mn') ? 'mn' : 'en';
	/**
	 * 리프레시 토큰으로 액세스 토큰 재발급 API
	 * 
	 * @description
	 * POST /auth/access-token
	 * 리프레시 토큰을 사용하여 새로운 액세스 토큰을 발급받습니다.
	 * 
	 * @param refreshToken - 리프레시 토큰
	 * @returns Promise<CreateAccessTokenByRefreshTokenResponseDto> - { accessToken: string }
	 * 
	 * @throws
	 * - 401: 유효하지 않은 리프레시 토큰입니다.
	 * - 404: 존재하지 않는 유저입니다.
	 * 
	 * @example
	 * const result = await refreshAccessToken('refresh_token_here');
	 * localStorage.setItem('accessToken', result.accessToken);
	 */
	const refreshAccessToken = async (refreshToken: string): Promise<CreateAccessTokenByRefreshTokenResponseDto> => {
		try {
			return await apiClient.post<CreateAccessTokenByRefreshTokenResponseDto>(
				'/auth/access-token',
				{ refreshToken } as CreateAccessTokenByRefreshTokenRequestDto,
				{ skipAuth: true },
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 401) throw new Error('유효하지 않은 리프레시 토큰입니다.');
				if (error.status === 404) throw new Error('존재하지 않는 유저입니다.');
			}
			throw new Error(msg || '토큰 재발급에 실패했습니다.');
		}
	};

	/**
	 * 이메일 인증 코드 발송 API
	 * 
	 * @description
	 * POST /auth/send-verification-email
	 * 이메일 인증을 위한 6자리 코드를 발송합니다.
	 * 
	 * @param email - 이메일 주소
	 * @returns Promise<SendVerificationEmailResponseDto> - { message: string }
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await sendVerificationEmail('user@example.com');
	 * // result.message 예: "인증 메일이 발송되었습니다."
	 */
	const sendVerificationEmail = async (email: string): Promise<SendVerificationEmailResponseDto> => {
		try {
			return await apiClient.post<SendVerificationEmailResponseDto>(
				'/auth/send-verification-email',
				{ email, language: currentLanguage as 'ko' | 'en' | 'ar' | 'id' | 'mn' } as SendVerificationEmailRequestDto,
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 401) throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
				if (error.status === 400) throw new Error(msg || '유효하지 않은 이메일 주소입니다.');
				if (error.status === 409) throw new Error(msg || '이미 인증된 이메일입니다. 다시 인증할 필요가 없습니다.');
				if (error.status === 500) throw new Error(msg || '이미 인증된 이메일이거나 서버 오류가 발생했습니다.');
			}
			throw new Error(msg || '인증 메일 발송에 실패했습니다.');
		}
	};

	/**
	 * 이메일 중복 확인 API
	 *
	 * @description
	 * POST /user/check-email
	 * 이메일 사용 가능 여부를 확인합니다.
	 */
	const checkEmail = async (email: string): Promise<CheckEmailResponseDto> => {
		try {
			return await apiClient.post<CheckEmailResponseDto>(
				'/user/check-email',
				{ email },
				{ skipAuth: true },
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError && error.status === 400) {
				throw new Error(msg || '이메일 중복 확인 요청이 올바르지 않습니다.');
			}
			throw new Error(msg || '이메일 중복 확인에 실패했습니다.');
		}
	};

	/**
	 * 이메일 인증 코드 검증 API
	 * 
	 * @description
	 * POST /auth/verify-verification-code
	 * 이메일로 받은 6자리 코드를 검증합니다.
	 * 
	 * @param email - 이메일 주소
	 * @param verificationCode - 6자리 인증 코드
	 * @returns Promise<VerifyVerificationCodeResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 400: 인증 코드가 일치하지 않거나 만료되었습니다.
	 * - 401: 유효하지 않은 토큰입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await verifyVerificationCode('user@example.com', '123456');
	 * // result.message 예: "이메일 인증이 완료되었습니다."
	 */
	const verifyVerificationCode = async (email: string, verificationCode: string): Promise<VerifyVerificationCodeResponseDto> => {
		try {
			return await apiClient.post<VerifyVerificationCodeResponseDto>(
				'/auth/verify-verification-code',
				{ email, verificationCode } as VerifyVerificationCodeRequestDto,
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 400) throw new Error(msg || '인증 코드가 일치하지 않거나 만료되었습니다.');
				if (error.status === 401) throw new Error('유효하지 않은 토큰입니다. 다시 로그인해주세요.');
				if (error.status === 409) throw new Error(msg || '이미 인증된 이메일입니다.');
				if (error.status >= 500 || error.status === 0) throw new Error('이미 인증된 이메일이거나 서버 오류가 발생했습니다. 이미 인증을 완료한 경우 다시 인증할 필요가 없습니다.');
			}
			throw new Error(msg || '인증 코드 검증에 실패했습니다.');
		}
	};

	/**
	 * 비밀번호 재설정 요청 API
	 * 
	 * @description
	 * POST /auth/password-reset
	 * 비밀번호 재설정을 위한 resetToken을 이메일로 발송합니다.
	 * 
	 * @param email - 이메일 주소
	 * @returns Promise<PasswordResetRequestResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 404: 존재하지 않는 사용자입니다.
	 * 
	 * @example
	 * const result = await requestPasswordReset('user@example.com');
	 * // result.message 예: "비밀번호 재설정 메일이 발송되었습니다."
	 */
	const requestPasswordReset = async (email: string): Promise<PasswordResetRequestResponseDto> => {
		try {
			return await apiClient.post<PasswordResetRequestResponseDto>(
				'/auth/password-reset',
				{ email, language: currentLanguage as 'ko' | 'en' | 'ar' | 'id' | 'mn' } as PasswordResetRequestDto,
				{ skipAuth: true },
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError && error.status === 404) throw new Error('존재하지 않는 사용자입니다.');
			throw new Error(msg || '비밀번호 재설정 요청에 실패했습니다.');
		}
	};

	/**
	 * 비밀번호 변경 API
	 * 
	 * @description
	 * POST /user/change-password
	 * resetToken을 사용하여 새로운 비밀번호로 변경합니다.
	 * 
	 * @param resetToken - 리셋 토큰 (이메일로 받은 토큰)
	 * @param newPassword - 새 비밀번호 (최소 8자)
	 * @returns Promise<ChangePasswordResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 400: 유효하지 않거나 만료된 리셋 토큰입니다.
	 * - 404: 사용자를 찾을 수 없습니다.
	 * 
	 * @example
	 * const result = await changePassword('reset_token_here', 'newPassword123');
	 * // result.message 예: "비밀번호가 성공적으로 변경되었습니다."
	 */
	const changePassword = async (resetToken: string, newPassword: string): Promise<ChangePasswordResponseDto> => {
		const token = resetToken.trim();
		if (!token) {
			throw new Error('유효하지 않거나 만료된 리셋 토큰입니다.');
		}
		try {
			return await apiClient.post<ChangePasswordResponseDto>(
				'/user/change-password',
				{ resetToken: token, newPassword } satisfies ChangePasswordRequestDto,
				{ skipAuth: true },
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 400) throw new Error(msg || '유효하지 않거나 만료된 리셋 토큰입니다.');
				if (error.status === 404) throw new Error('사용자를 찾을 수 없습니다.');
			}
			throw new Error(msg || '비밀번호 변경에 실패했습니다.');
		}
	};

	/**
	 * 계정 삭제 인증 코드 발송 API
	 * 
	 * @description
	 * POST /auth/send-deletion-code
	 * 계정 삭제를 위한 6자리 인증 코드를 이메일로 발송합니다.
	 * 
	 * @returns Promise<SendDeletionCodeResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 400: 이메일이 등록되어 있지 않습니다.
	 * - 401: 유효하지 않은 토큰입니다.
	 * - 404: 존재하지 않는 사용자입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await sendDeletionCode();
	 * // result.message 예: "계정 삭제 인증 코드가 발송되었습니다."
	 */
	const sendDeletionCode = async (): Promise<SendDeletionCodeResponseDto> => {
		try {
			return await apiClient.post<SendDeletionCodeResponseDto>(
				'/auth/send-deletion-code',
				undefined,
				{ params: { language: currentLanguage } }
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 400) throw new Error(msg || '이메일이 등록되어 있지 않습니다.');
				if (error.status === 401) throw new Error('유효하지 않은 토큰입니다.');
				if (error.status === 404) throw new Error('존재하지 않는 사용자입니다.');
			}
			throw new Error(msg || '인증 코드 발송에 실패했습니다.');
		}
	};

	/**
	 * 계정 삭제 인증 코드 검증 API
	 * 
	 * @description
	 * POST /auth/verify-deletion-code
	 * 이메일로 받은 계정 삭제 인증 코드를 검증합니다.
	 * 
	 * @param deletionCode - 6자리 인증 코드
	 * @returns Promise<VerifyDeletionCodeResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 400: 인증 코드가 일치하지 않거나 만료되었습니다.
	 * - 401: 유효하지 않은 토큰입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await verifyDeletionCode('123456');
	 * // result.message 예: "인증 코드가 확인되었습니다."
	 */
	const verifyDeletionCode = async (deletionCode: string): Promise<VerifyDeletionCodeResponseDto> => {
		try {
			return await apiClient.post<VerifyDeletionCodeResponseDto>(
				'/auth/verify-deletion-code',
				{ deletionCode } as VerifyDeletionCodeRequestDto,
			);
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			if (error instanceof ApiError) {
				if (error.status === 400) throw new Error(msg || '인증 코드가 일치하지 않거나 만료되었습니다.');
				if (error.status === 401) throw new Error('유효하지 않은 토큰입니다.');
			}
			throw new Error(msg || '인증 코드 검증에 실패했습니다.');
		}
	};

	/**
	 * 이용약관 리스트 조회 API
	 * 
	 * @description
	 * GET /terms/list
	 * 서비스 이용약관 및 개인정보 처리방침 리스트를 조회합니다.
	 * 
	 * @returns Promise<GetTermsListResponseDto> - { termsList: TermsListItemDto[] }
	 * 
	 * @example
	 * const result = await getTermsList();
	 * // result.termsList: 약관 항목 배열
	 */
	const getTermsList = async (): Promise<GetTermsListResponseDto> => {
		try {
			return await apiClient.get<GetTermsListResponseDto>('/terms/list', { skipAuth: true });
		} catch (error) {
			const msg = extractApiErrorMessage(error);
			throw new Error(msg || '이용약관 조회에 실패했습니다.');
		}
	};

	return {
		refreshAccessToken,
		checkEmail,
		sendVerificationEmail,
		verifyVerificationCode,
		requestPasswordReset,
		changePassword,
		sendDeletionCode,
		verifyDeletionCode,
		getTermsList,
	};
};
